import type { WebPushSubscriptionInput } from "@/api/notifications";
import { GLOBAL_CONFIG } from "@/global-config";
import { getTickbirdServiceWorkerRegistration, registerTickbirdServiceWorker } from "@/service-worker";

const WEB_PUSH_RENEWAL_CACHE = "tickbird-web-push-renewal-v1";
const WEB_PUSH_RENEWAL_CONFIG_PATH = "/__tickbird_internal__/web-push-renewal";
const WEB_PUSH_RENEWAL_LOCK = "tickbird-web-push-renewal-state";

type WebPushRenewalState = {
  version: 1;
  endpoint: string;
  renewalUrl: string;
  renewalToken: string;
  applicationServerKey: string;
  renewalRequired: false;
  pendingSubscription: null;
};

let renewalEpoch = 0;
let renewalCacheQueue: Promise<unknown> = Promise.resolve();

function requestRenewalStateLock<T>(operation: () => Promise<T>): Promise<T> {
  if (typeof navigator !== "undefined" && "locks" in navigator && navigator.locks && typeof navigator.locks.request === "function") {
    return navigator.locks.request(WEB_PUSH_RENEWAL_LOCK, { mode: "exclusive" }, operation);
  }
  return operation();
}

function serializeRenewalCacheMutation<T>(operation: () => Promise<T>): Promise<T> {
  const run = () => requestRenewalStateLock(operation);
  const result = renewalCacheQueue.then(run, run);
  renewalCacheQueue = result.then(
    () => undefined,
    () => undefined,
  );
  return result;
}

export type WebPushUnsupportedReason = "insecure-context" | "notifications" | "service-worker" | "push-manager";

export type WebPushSupport = { supported: true } | { supported: false; reason: WebPushUnsupportedReason };

export function getWebPushSupport(): WebPushSupport {
  if (typeof window === "undefined" || !window.isSecureContext) {
    return { supported: false, reason: "insecure-context" };
  }
  if (!("Notification" in window)) {
    return { supported: false, reason: "notifications" };
  }
  if (!("serviceWorker" in navigator)) {
    return { supported: false, reason: "service-worker" };
  }
  if (!("PushManager" in window)) {
    return { supported: false, reason: "push-manager" };
  }
  return { supported: true };
}

export function decodeApplicationServerKey(value: string): Uint8Array<ArrayBuffer> {
  const normalized = value.trim().replace(/-/g, "+").replace(/_/g, "/");
  const padding = "=".repeat((4 - (normalized.length % 4)) % 4);
  const binary = window.atob(`${normalized}${padding}`);
  const bytes = new Uint8Array(binary.length);

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  return bytes;
}

function encodeArrayBuffer(value: ArrayBuffer): string {
  const bytes = new Uint8Array(value);
  let binary = "";
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return window.btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/u, "");
}

export function serializeWebPushSubscription(subscription: PushSubscription): WebPushSubscriptionInput {
  const json = subscription.toJSON();
  const p256dhKey = subscription.getKey("p256dh");
  const authKey = subscription.getKey("auth");
  const p256dh = json.keys?.p256dh ?? (p256dhKey ? encodeArrayBuffer(p256dhKey) : null);
  const auth = json.keys?.auth ?? (authKey ? encodeArrayBuffer(authKey) : null);

  if (!subscription.endpoint || !p256dh || !auth) {
    throw new Error("The browser returned an incomplete Web Push subscription.");
  }

  return {
    endpoint: subscription.endpoint,
    expirationTime: subscription.expirationTime === null ? null : new Date(subscription.expirationTime).toISOString(),
    keys: { p256dh, auth },
  };
}

export function webPushSubscriptionUsesKey(subscription: PushSubscription, applicationServerKey: Uint8Array<ArrayBuffer>): boolean {
  const currentKey = subscription.options.applicationServerKey;
  if (!currentKey) {
    return false;
  }

  const currentBytes = new Uint8Array(currentKey);
  if (currentBytes.byteLength !== applicationServerKey.byteLength) {
    return false;
  }

  return currentBytes.every((byte, index) => byte === applicationServerKey[index]);
}

export async function getExistingWebPushSubscription(): Promise<PushSubscription | null> {
  if (getWebPushSupport().supported === false) {
    return null;
  }

  const registration = await getReadyWebPushRegistration();
  return registration.pushManager.getSubscription();
}

export async function getReadyWebPushRegistration(): Promise<ServiceWorkerRegistration> {
  return registerTickbirdServiceWorker();
}

function renewalConfigUrl(): string {
  return new URL(WEB_PUSH_RENEWAL_CONFIG_PATH, window.location.origin).toString();
}

function renewalEndpointUrl(): string {
  return new URL(`${GLOBAL_CONFIG.apiBaseUrl}/notifications/web-push/subscriptions/renew`, window.location.origin).toString();
}

export async function registerAndConfigureWebPushRenewal(
  subscription: PushSubscription,
  applicationServerKey: string,
  registerSubscription: () => Promise<string>,
): Promise<boolean> {
  const expectedEpoch = renewalEpoch;
  return serializeRenewalCacheMutation(async () => {
    if (expectedEpoch !== renewalEpoch) {
      return false;
    }

    const registration = await getTickbirdServiceWorkerRegistration();
    const currentSubscription = await registration?.pushManager.getSubscription();
    if (!currentSubscription) {
      return false;
    }
    const expected = serializeWebPushSubscription(subscription);
    const current = serializeWebPushSubscription(currentSubscription);
    if (expected.endpoint !== current.endpoint || expected.keys.p256dh !== current.keys.p256dh || expected.keys.auth !== current.keys.auth) {
      return false;
    }

    // Keep the authenticated token rotation and Cache Storage write in one
    // cross-context critical section so another tab or the service worker cannot
    // publish an older renewal credential after this request commits.
    const renewalToken = await registerSubscription();
    if (expectedEpoch !== renewalEpoch) {
      return false;
    }

    const state: WebPushRenewalState = {
      version: 1,
      endpoint: subscription.endpoint,
      renewalUrl: renewalEndpointUrl(),
      renewalToken,
      applicationServerKey: applicationServerKey.trim(),
      renewalRequired: false,
      pendingSubscription: null,
    };
    const cache = await window.caches.open(WEB_PUSH_RENEWAL_CACHE);
    await cache.put(
      renewalConfigUrl(),
      new Response(JSON.stringify(state), {
        headers: {
          "Cache-Control": "no-store",
          "Content-Type": "application/json",
        },
      }),
    );
    return true;
  });
}

type WebPushDetachResult = {
  hadSubscription: boolean;
  serverFailed: boolean;
};

async function readRenewalIdentity(): Promise<{ endpoint: string | null; renewalToken: string | null }> {
  try {
    const cache = await window.caches.open(WEB_PUSH_RENEWAL_CACHE);
    const response = await cache.match(renewalConfigUrl());
    if (!response) {
      return { endpoint: null, renewalToken: null };
    }
    const value = (await response.json()) as Partial<WebPushRenewalState>;
    const endpoint = typeof value.endpoint === "string" && value.endpoint ? value.endpoint : null;
    const renewalToken = typeof value.renewalToken === "string" && /^[A-Za-z0-9_-]{43}$/u.test(value.renewalToken) ? value.renewalToken : null;
    return { endpoint, renewalToken };
  } catch {
    return { endpoint: null, renewalToken: null };
  }
}

export async function detachRegisteredWebPushSubscription(
  removeFromServer?: (endpoint: string, renewalToken: string | null) => Promise<void>,
): Promise<WebPushDetachResult> {
  renewalEpoch += 1;
  if (typeof window === "undefined" || !("caches" in window)) {
    return { hadSubscription: false, serverFailed: false };
  }

  return serializeRenewalCacheMutation(async () => {
    const renewalIdentity = await readRenewalIdentity();
    const registration = await getTickbirdServiceWorkerRegistration();
    const subscription = await registration?.pushManager.getSubscription();
    const endpoint = subscription?.endpoint ?? renewalIdentity.endpoint;
    await window.caches.delete(WEB_PUSH_RENEWAL_CACHE);

    let serverFailed = false;
    if (endpoint && removeFromServer) {
      try {
        await removeFromServer(endpoint, renewalIdentity.renewalToken);
      } catch {
        serverFailed = true;
      }
    }

    if (subscription && !(await subscription.unsubscribe())) {
      throw new Error("The browser could not remove the Web Push subscription.");
    }

    return { hadSubscription: Boolean(subscription), serverFailed };
  });
}

/** Best-effort local cleanup for session loss, where authenticated API access is no longer safe to assume. */
export async function detachWebPushLocally(): Promise<void> {
  try {
    await detachRegisteredWebPushSubscription();
  } catch (error) {
    console.warn("Could not remove the local Web Push subscription after the session ended.", error);
  }
}
